import React, { ReactElement, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Formik } from 'formik';
import { sanitize } from 'dompurify';

import styles from './styles.module.css';
import {
  useAdminCheckPin,
  useAdminResendPinCode,
  useAdminSignIn,
} from '../../graphql/auth/useUserSignIn/admin';
import {
  useHcpCheckPin,
  useHcpResendPinCode,
  useHcpSignIn,
} from '../../graphql/auth/useUserSignIn/hcp';
import { getCurrenLang } from '../../redux/selector';
import { LoginForm } from '../Form/LoginForm';
import { Loading, CustomLink, FormFormik, SectionTitle } from '../../common';
import { SignUpAccountVerified } from '../Form/SignUp';
import { validationPIN, validationSchema } from '../../utils/validators';
import { initialCode, initialValuesSignIn } from './initValues';
import storage from '../../utils/storage';
import { path } from '../../utils/routers/book';
import { Form } from '../../graphql/auth/types';
import { delay } from '../../utils/helper';
import { FormValuesCode } from './types';
import LogoPublic from '../../common/Logo/LogoPublic';
import Bubble from '../../common/Logo/Bubble';

type TProps = {
  isAdmin: boolean
}

const SignIn = ({ isAdmin }: TProps): ReactElement => {
  const t: any = useSelector<any>((state) => getCurrenLang(state));
  const welcome_to_otm = t?.auth.welcome_to_otm || 'Welcome to On The Mend';
  const achieve_more = t?.auth.achieve_more || 'Achieve more';
  const resend_pin_code_to_email = t?.validation.resend_pin_code_to_email;
  const pin_code_is_going_to_be_sent_to = t?.auth.auth_pin_code;
  const pin_code_is_going_to_be_sent_to_for_HCP = t?.auth.auth_pin_code_to_phone_and_email || '';
  const [resendPending, setResendPending] = useState<boolean>(false);
  const [candidatEmail, setCandidatEmail] = useState<string>('');
  const history = useHistory();
  // const store = storage.get('user');

  const asAdmin = isAdmin;
  const asHcp = !isAdmin;

  // First step auth
  const {
    adminFirstStepAuthLoading,
    _signInFirstStepAuthAdmin,
    errorMessageFirstStepAuthAdmin,
    authorizedFirstStepAuthAdmin,
  } = useAdminSignIn();
  const {
    _signInFirstStepAuthHcp,
    firstStepAuthLoadingHcp,
    errorMessageFirstStepAuthHcp,
    firstStepAuthAuthorizedHcp,
  } = useHcpSignIn();

  const sendHcp = (customer: Form): void => {
    setCandidatEmail(customer.email);
    _signInFirstStepAuthHcp(customer);
  };
  const sendAdmin = (customer: Form): void => {
    setCandidatEmail(customer.email);
    _signInFirstStepAuthAdmin(customer);
  };

  // Second step auth
  const {
    setErrorMessage,
    _authHcpCheckPin,
    authHcpCheckPinLoading,
    authHcpCheckPinError,
  } = useHcpCheckPin();
  const { _resendPinCode, loadingSensPin } = useHcpResendPinCode();
  const {
    setErrorMessageAdmin,
    _authAdminCheckPin,
    authAdminCheckPinLoading,
    authAdminCheckPinError,
  } = useAdminCheckPin();
  const { _resendPinCodeAdmin, loadingResendPinAdmin } = useAdminResendPinCode();

  const sendCode = (paramets: FormValuesCode, { resetForm }: any): void => {
    const pinCode = paramets ? paramets.code.trim() : '';
    const data = {
      email: candidatEmail,
      pinCode,
    };
    if (asHcp) {
      setErrorMessage('');
      _authHcpCheckPin(data);
      // onLogIn(store);
      storage.save('show-modal-date', '');
    }
    if (asAdmin) {
      setErrorMessageAdmin('');
      _authAdminCheckPin(data);
      // onLogIn(store);
    }
    resetForm();
  };

  // Resend pin
  const onResendPin = async (): Promise<void> => {
    if (asHcp) {
      _resendPinCode(candidatEmail);
      setErrorMessage('');
    }
    if (asAdmin) {
      _resendPinCodeAdmin(candidatEmail);
      setErrorMessageAdmin('');
    }
    setResendPending(true);
    await delay(30000).then((res: any) => setResendPending(res.message));
  };

  // Reset password
  const resetPasswordHcp = (): void => {
    history.push(path.resetEmail);
  };

  const resetPasswordAdmin = (): void => {
    history.push(path.resetAdminEmail);
  };

  const getText = (response: { phone: string; email: string }): string => {
    const phone = response?.phone || '';
    const email = response?.email || '';
    let pin_code_is_going_to_be_sent_to_your_email = '';
    if (asAdmin) {
      return `${pin_code_is_going_to_be_sent_to} <b>${authorizedFirstStepAuthAdmin}</b>`;
    }
    pin_code_is_going_to_be_sent_to_your_email = pin_code_is_going_to_be_sent_to_for_HCP
      .replace('<phone>', `<b>${phone}</b>`)
      .replace('<email>', `<b>${email}</b>`);

    return pin_code_is_going_to_be_sent_to_your_email;
  };

  /* JSX */
  const loaderJSX = (firstStepAuthLoadingHcp ||
    adminFirstStepAuthLoading ||
    authHcpCheckPinLoading ||
    loadingSensPin ||
    authAdminCheckPinLoading ||
    loadingResendPinAdmin) && <Loading />;

  const firstStepJSX = !authorizedFirstStepAuthAdmin && !firstStepAuthAuthorizedHcp && (
    <>
      <SectionTitle title={welcome_to_otm} />
      <div className={styles['auth-subtitle']}>{achieve_more}</div>
      <FormFormik
        initialValues={initialValuesSignIn}
        schema={(): any => validationSchema(t)}
        onSend={asAdmin ? sendAdmin : sendHcp}
        errorMessage={errorMessageFirstStepAuthHcp || errorMessageFirstStepAuthAdmin}
        component={LoginForm}
      />
      <CustomLink
        buttonMethod={asAdmin ? resetPasswordAdmin : resetPasswordHcp}
        buttonName={`${t?.common.forgot || 'Forgot password?'}`}
      />
    </>
  );

  const secondStepJSX = (firstStepAuthAuthorizedHcp || authorizedFirstStepAuthAdmin) &&
    !errorMessageFirstStepAuthAdmin &&
    !errorMessageFirstStepAuthHcp && (
      <>
        <SectionTitle title={`${t && t.auth.two_step_verification}`} />
        <div className={styles.verifiedWrapper}>
          <div className={styles.section}>
            <p
              dangerouslySetInnerHTML={{
                __html: sanitize(
                  getText(firstStepAuthAuthorizedHcp || authorizedFirstStepAuthAdmin),
                ),
              }}
            />
          </div>
          <Formik
            initialValues={initialCode}
            onSubmit={sendCode}
            validationSchema={(): any => validationPIN(t)}
            render={(props): any => (
              <SignUpAccountVerified
                {...props}
                resendMethod={onResendPin}
                resendPending={resendPending}
                error={authHcpCheckPinError || authAdminCheckPinError}
                resendPinCodeMessage={resend_pin_code_to_email}
              />
            )}
          />
        </div>
      </>
    );

  return (
    <>
      {loaderJSX}
      <div className={styles.main_container}>
        <LogoPublic />
        {firstStepJSX}
        {secondStepJSX}
      </div>
      <Bubble />
    </>
  );
};

// export default withAuthContext(SignIn);
export default SignIn;
